import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import axios from 'axios'
import { Bug, Plus, Image as ImageIcon, X, Loader, Clipboard, Upload, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'

const Page = styled.div`
  padding: 28px 32px 80px;
  background: ${p => p.theme.backgroundColor};
  min-height: calc(100vh - 64px);
  box-sizing: border-box;

  @media (max-width: 768px) { padding: 20px 16px 60px; }
`

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`

const TitleBox = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  .icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #ef4444 0%, #f97316 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.30);
  }

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 800;
    color: ${p => p.theme.textPrimary};
    letter-spacing: -0.4px;
  }
  p {
    margin: 4px 0 0;
    font-size: 13px;
    color: ${p => p.theme.textSecondary};
  }
`

const NewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 20px;
  background: linear-gradient(135deg, #ef4444 0%, #f97316 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(239, 68, 68, 0.30);
  transition: transform 0.15s;
  &:hover { transform: translateY(-1px); }
`

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`

const Chip = styled.button`
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid ${p => p.$active ? 'transparent' : p.theme.border};
  background: ${p => p.$active ? 'linear-gradient(135deg, #ef4444, #f97316)' : p.theme.cardBackground};
  color: ${p => p.$active ? '#fff' : p.theme.textSecondary};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #ef4444; color: ${p => p.$active ? '#fff' : '#ef4444'}; }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Card = styled.div`
  background: ${p => p.theme.cardBackground};
  border: 1px solid ${p => p.theme.border};
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  transition: all 0.15s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  &:hover {
    border-color: #fca5a5;
    box-shadow: 0 8px 20px rgba(239,68,68,0.08);
  }

  @media (max-width: 600px) { flex-direction: column; }
`

const Thumb = styled.div`
  width: 110px;
  height: 80px;
  border-radius: 10px;
  background: ${p => p.$image ? `url(${p.$image}) center/cover` : p.theme.hoverBackground};
  border: 1px solid ${p => p.theme.border};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.textMuted};
  cursor: ${p => p.$image ? 'zoom-in' : 'default'};

  @media (max-width: 600px) {
    width: 100%;
    height: 160px;
  }
`

const Body = styled.div`
  flex: 1;
  min-width: 0;

  .meta-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }
  h3 {
    margin: 0 0 6px;
    font-size: 15.5px;
    font-weight: 700;
    color: ${p => p.theme.textPrimary};
    line-height: 1.35;
  }
  p {
    margin: 0;
    font-size: 13px;
    color: ${p => p.theme.textSecondary};
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: pre-wrap;
  }
  .footer {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 11.5px;
    color: ${p => p.theme.textMuted};
  }
`

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${p => p.$bg || 'rgba(239,68,68,0.12)'};
  color: ${p => p.$color || '#ef4444'};
`

const StatusBadge = styled(Tag)``

const Empty = styled.div`
  padding: 70px 20px;
  text-align: center;
  background: ${p => p.theme.cardBackground};
  border: 1px dashed ${p => p.theme.border};
  border-radius: 14px;
  color: ${p => p.theme.textMuted};

  .icon { color: #fca5a5; margin-bottom: 10px; }
  .title { font-size: 15px; font-weight: 700; color: ${p => p.theme.textSecondary}; margin-bottom: 4px; }
  .desc { font-size: 12.5px; }
`

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`

const Modal = styled.div`
  background: ${p => p.theme.cardBackground};
  width: min(640px, 100%);
  max-height: 90vh;
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 30px 60px rgba(0,0,0,0.25);
`

const ModalHead = styled.div`
  padding: 20px 22px;
  border-bottom: 1px solid ${p => p.theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2 {
    margin: 0;
    font-size: 17px;
    font-weight: 800;
    color: ${p => p.theme.textPrimary};
    display: flex;
    align-items: center;
    gap: 8px;
  }
  button {
    width: 32px; height: 32px;
    background: ${p => p.theme.hoverBackground};
    border: none;
    border-radius: 8px;
    cursor: pointer;
    color: ${p => p.theme.textSecondary};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &:hover { background: ${p => p.theme.borderLight}; color: ${p => p.theme.textPrimary}; }
  }
`

const ModalBody = styled.div`
  padding: 22px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12.5px;
    font-weight: 700;
    color: ${p => p.theme.textSecondary};
  }
`

const Input = styled.input`
  width: 100%;
  padding: 11px 14px;
  border-radius: 10px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.inputBackground};
  color: ${p => p.theme.textPrimary};
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: #ef4444; }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.inputBackground};
  color: ${p => p.theme.textPrimary};
  font-size: 14px;
  min-height: 130px;
  resize: vertical;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: #ef4444; }
`

const CategoryRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const CatPill = styled.button`
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid ${p => p.$active ? '#ef4444' : p.theme.border};
  background: ${p => p.$active ? 'rgba(239,68,68,0.10)' : p.theme.cardBackground};
  color: ${p => p.$active ? '#ef4444' : p.theme.textSecondary};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
`

const Dropzone = styled.div`
  position: relative;
  border: 2px dashed ${p => p.$hasImage ? '#ef4444' : p.theme.border};
  border-radius: 12px;
  padding: ${p => p.$hasImage ? '10px' : '22px 14px'};
  background: ${p => p.theme.hoverBackground};
  cursor: pointer;
  transition: border-color 0.15s;
  &:hover { border-color: #ef4444; }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: ${p => p.theme.textMuted};
    font-size: 12.5px;
    text-align: center;
    line-height: 1.5;
    .row {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: ${p => p.theme.textSecondary};
      font-weight: 700;
    }
    .hint {
      font-size: 11.5px;
      color: ${p => p.theme.textMuted};
    }
  }
`

const Preview = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  max-height: 280px;
  display: flex;
  justify-content: center;
  background: #000;

  img { max-width: 100%; max-height: 280px; object-fit: contain; display: block; }

  .remove {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: rgba(15, 23, 42, 0.7);
    color: #fff;
    border: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &:hover { background: rgba(239,68,68,0.85); }
  }
`

const HiddenFile = styled.input`
  display: none;
`

const ModalFoot = styled.div`
  padding: 16px 22px;
  border-top: 1px solid ${p => p.theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  .left {
    font-size: 11.5px;
    color: ${p => p.theme.textMuted};
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .right {
    display: inline-flex;
    gap: 8px;
  }
`

const SubmitBtn = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #ef4444 0%, #f97316 100%);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const CancelBtn = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.cardBackground};
  color: ${p => p.theme.textSecondary};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover { color: ${p => p.theme.textPrimary}; }
`

const LightboxBackdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.85);
  z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  cursor: zoom-out;

  img { max-width: 100%; max-height: 100%; }
`

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 11000;
  background: ${p => p.$ok ? '#10b981' : '#ef4444'};
  color: #fff;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 12px 24px rgba(0,0,0,0.25);
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const CATEGORIES = [
  { key: 'all', label: '전체' },
  { key: 'game', label: '게임' },
  { key: 'qr', label: 'QR/스캔' },
  { key: 'ui', label: 'UI/표시' },
  { key: 'login', label: '로그인/계정' },
  { key: 'performance', label: '성능' },
  { key: 'general', label: '기타' },
]

const STATUS_META = {
  open: { label: '접수', bg: 'rgba(239,68,68,0.12)', color: '#ef4444' },
  in_progress: { label: '처리중', bg: 'rgba(245,158,11,0.14)', color: '#d97706' },
  resolved: { label: '해결', bg: 'rgba(16,185,129,0.14)', color: '#10b981' },
}

const formatDate = (s) => {
  if (!s) return ''
  const d = new Date(s)
  if (isNaN(d.getTime())) return ''
  const now = new Date()
  const diff = (now - d) / 1000
  if (diff < 60) return '방금 전'
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}

export default function BugReportPage() {
  const { theme } = useTheme()
  const { user, isLoggedIn } = useAuth()

  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [showModal, setShowModal] = useState(false)
  const [lightbox, setLightbox] = useState(null)
  const [toast, setToast] = useState(null)

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState('general')
  const [screenshotPreview, setScreenshotPreview] = useState(null)
  const [screenshotFile, setScreenshotFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = useRef(null)

  const showToast = (ok, message) => {
    setToast({ ok, message })
    setTimeout(() => setToast(null), 2500)
  }

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true)
      const res = await fetch('/api/bug-reports?limit=50', { credentials: 'include' })
      const data = await res.json()
      if (data.success) setReports(data.reports || [])
    } catch (err) {
      console.error('버그 제보 조회 실패:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchReports() }, [fetchReports])

  const resetForm = () => {
    setTitle('')
    setContent('')
    setCategory('general')
    setScreenshotPreview(null)
    setScreenshotFile(null)
  }

  const closeModal = () => {
    if (submitting || uploading) return
    setShowModal(false)
    resetForm()
  }

  const handleFile = (file) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      showToast(false, '이미지 파일만 첨부 가능합니다.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast(false, '이미지는 10MB 이하만 가능합니다.')
      return
    }
    setScreenshotFile(file)
    const reader = new FileReader()
    reader.onload = (e) => setScreenshotPreview(e.target.result)
    reader.readAsDataURL(file)
  }

  // 모달 안에서 Ctrl+V 페이스트 → 스크린샷 첨부
  useEffect(() => {
    if (!showModal) return
    const handler = (e) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) {
            handleFile(file)
            e.preventDefault()
            return
          }
        }
      }
    }
    window.addEventListener('paste', handler)
    return () => window.removeEventListener('paste', handler)
  }, [showModal])

  const uploadScreenshot = async () => {
    if (!screenshotFile) return null
    const formData = new FormData()
    formData.append('file', screenshotFile)
    formData.append('uploadId', `bugreport_${Date.now()}`)
    const res = await axios.post('/ObjectUpload/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return res.data?.data?.data?.files?.[0]?.url || null
  }

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) {
      showToast(false, '제목과 내용을 입력해주세요.')
      return
    }
    try {
      setSubmitting(true)
      let screenshotUrl = null
      if (screenshotFile) {
        setUploading(true)
        try {
          screenshotUrl = await uploadScreenshot()
        } catch (err) {
          console.error('스크린샷 업로드 실패:', err)
          showToast(false, '스크린샷 업로드에 실패했습니다.')
          setUploading(false)
          setSubmitting(false)
          return
        }
        setUploading(false)
      }

      const res = await fetch('/api/bug-reports', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          category,
          screenshotUrl,
          pageUrl: window.location.href
        })
      })
      const data = await res.json()
      if (!data.success) throw new Error(data.error || '등록 실패')

      showToast(true, '버그 제보가 등록되었습니다. 감사합니다!')
      setShowModal(false)
      resetForm()
      fetchReports()
    } catch (err) {
      console.error('버그 제보 등록 실패:', err)
      showToast(false, '제보 등록에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  const filtered = filter === 'all'
    ? reports
    : reports.filter(r => r.category === filter)

  return (
    <Page theme={theme}>
      <Header>
        <TitleBox theme={theme}>
          <div className="icon"><Bug size={24} /></div>
          <div>
            <h1>버그 제보</h1>
            <p>문제를 발견하셨나요? 캡처와 함께 알려주시면 빠르게 해결하겠습니다.</p>
          </div>
        </TitleBox>
        <NewButton onClick={() => setShowModal(true)}>
          <Plus size={16} /> 버그 제보하기
        </NewButton>
      </Header>

      <FilterRow>
        {CATEGORIES.map(c => (
          <Chip
            key={c.key}
            theme={theme}
            $active={filter === c.key}
            onClick={() => setFilter(c.key)}
          >
            {c.label}
          </Chip>
        ))}
      </FilterRow>

      {loading ? (
        <Empty theme={theme}>
          <Loader className="icon" size={36} />
          <div className="title">불러오는 중...</div>
        </Empty>
      ) : filtered.length === 0 ? (
        <Empty theme={theme}>
          <Bug className="icon" size={42} />
          <div className="title">아직 제보된 버그가 없어요</div>
          <div className="desc">첫 번째 제보자가 되어주세요!</div>
        </Empty>
      ) : (
        <List>
          {filtered.map(r => {
            const status = STATUS_META[r.status] || STATUS_META.open
            const catLabel = CATEGORIES.find(c => c.key === r.category)?.label || '기타'
            return (
              <Card key={r.id} theme={theme}>
                <Thumb
                  theme={theme}
                  $image={r.screenshot_url}
                  onClick={() => r.screenshot_url && setLightbox(r.screenshot_url)}
                >
                  {!r.screenshot_url && <ImageIcon size={28} />}
                </Thumb>
                <Body theme={theme}>
                  <div className="meta-row">
                    <StatusBadge $bg={status.bg} $color={status.color}>{status.label}</StatusBadge>
                    <Tag $bg="rgba(99,102,241,0.12)" $color="#6366f1">{catLabel}</Tag>
                  </div>
                  <h3>{r.title}</h3>
                  <p>{r.content}</p>
                  <div className="footer">
                    <span>{r.username || '익명'}</span>
                    <span>·</span>
                    <span>{formatDate(r.created_at)}</span>
                  </div>
                </Body>
              </Card>
            )
          })}
        </List>
      )}

      {showModal && (
        <Backdrop onClick={closeModal}>
          <Modal theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalHead theme={theme}>
              <h2><Bug size={18} color="#ef4444" /> 버그 제보하기</h2>
              <button onClick={closeModal}><X size={16} /></button>
            </ModalHead>

            <ModalBody>
              <Field theme={theme}>
                <label>카테고리</label>
                <CategoryRow>
                  {CATEGORIES.filter(c => c.key !== 'all').map(c => (
                    <CatPill
                      key={c.key}
                      theme={theme}
                      $active={category === c.key}
                      onClick={() => setCategory(c.key)}
                    >
                      {c.label}
                    </CatPill>
                  ))}
                </CategoryRow>
              </Field>

              <Field theme={theme}>
                <label>제목 *</label>
                <Input
                  theme={theme}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="예: 게임방 인쇄용 QR이 인식되지 않습니다"
                  maxLength={200}
                />
              </Field>

              <Field theme={theme}>
                <label>상세 내용 *</label>
                <TextArea
                  theme={theme}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={'언제, 어떤 동작에서 발생했나요?\n재현 순서가 있다면 자세히 적어주세요.'}
                  maxLength={5000}
                />
              </Field>

              <Field theme={theme}>
                <label>스크린샷 (선택)</label>
                <Dropzone
                  theme={theme}
                  $hasImage={!!screenshotPreview}
                  onClick={() => !screenshotPreview && fileInputRef.current?.click()}
                >
                  {screenshotPreview ? (
                    <Preview>
                      <img src={screenshotPreview} alt="스크린샷" />
                      <button
                        className="remove"
                        onClick={(e) => {
                          e.stopPropagation()
                          setScreenshotPreview(null)
                          setScreenshotFile(null)
                        }}
                      >
                        <X size={14} />
                      </button>
                    </Preview>
                  ) : (
                    <div className="placeholder">
                      <span className="row"><Upload size={16} /> 클릭해서 이미지 선택</span>
                      <span className="hint">
                        또는 <Clipboard size={11} style={{ verticalAlign: 'middle' }} /> Ctrl+V 로 캡처 붙여넣기 (최대 10MB)
                      </span>
                    </div>
                  )}
                  <HiddenFile
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </Dropzone>
              </Field>
            </ModalBody>

            <ModalFoot theme={theme}>
              <div className="left">
                {isLoggedIn ? (
                  <><CheckCircle2 size={13} color="#10b981" /> {user?.username || user?.full_name} 으로 제보</>
                ) : (
                  <><AlertCircle size={13} /> 익명으로 제보됩니다</>
                )}
              </div>
              <div className="right">
                <CancelBtn theme={theme} onClick={closeModal} disabled={submitting}>취소</CancelBtn>
                <SubmitBtn onClick={handleSubmit} disabled={submitting}>
                  {submitting ? <><Loader size={14} /> {uploading ? '업로드 중...' : '등록 중...'}</> : <><Bug size={14} /> 제보하기</>}
                </SubmitBtn>
              </div>
            </ModalFoot>
          </Modal>
        </Backdrop>
      )}

      {lightbox && (
        <LightboxBackdrop onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="스크린샷 확대" />
        </LightboxBackdrop>
      )}

      {toast && (
        <Toast $ok={toast.ok}>
          {toast.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {toast.message}
        </Toast>
      )}
    </Page>
  )
}
